import type { ReactNode } from 'react';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="privacy-section">
    <h2>{title}</h2>
    {children}
  </section>
);

export function PrivacyPolicy() {
  return (
    <main className="privacy-page">
      <div className="privacy-card">
        <header className="privacy-header">
          <div className="privacy-brand" aria-label="UTAGE">UTAGE</div>
          <p className="privacy-kicker">PRIVACY POLICY</p>
          <h1>Human first. Private by design.</h1>
          <p className="privacy-lead">This policy explains what UTAGE processes when you use the Telegram bot, Mini App, games, connection features, quizzes, replays, Say It, Care, and safety tools.</p>
          <p className="privacy-date">Effective 17 August 2026</p>
        </header>

        <Section title="1. What UTAGE is">
          <p>UTAGE is a human-to-human social game that runs primarily as a Telegram Mini App. Telegram provides the account identity used to open UTAGE; UTAGE does not use a separate email-and-password login.</p>
        </Section>

        <Section title="2. Information we process">
          <p>Depending on the features you use, UTAGE may process:</p>
          <ul>
            <li><strong>Telegram account information:</strong> Telegram user ID, first and last name when available, username, language code, and Telegram profile-photo URL.</li>
            <li><strong>UTAGE profile information:</strong> a custom display name or profile image you choose to add.</li>
            <li><strong>Gameplay information:</strong> game sessions, answers, predictions, reactions, scores, challenge and group participation, rematches, quiz answers, custom quiz questions, connection history, and relationship-pattern summaries produced from supported gameplay signals.</li>
            <li><strong>Messages and scheduling:</strong> Say It content, Care message content, recipient, schedule, delivery status, and related actions.</li>
            <li><strong>Uploads and generated media:</strong> profile images, quiz images, and replay-video data when you request a replay.</li>
            <li><strong>Safety information:</strong> blocks, reports, moderation state, and the session or interaction context needed to review a safety action.</li>
            <li><strong>Technical and analytics information:</strong> session and action identifiers, timestamps, feature usage, app/platform context, recovery events, security signals, and operational logs.</li>
          </ul>
        </Section>

        <Section title="3. Why we process it">
          <p>We use this information to authenticate you, run and recover games, connect the right people, deliver invitations and scheduled messages, maintain your history and profile, generate requested replays, improve reliability and product design, prevent abuse, enforce safety controls, and investigate technical or moderation issues.</p>
        </Section>

        <Section title="4. What other people can see">
          <p>UTAGE only reveals information that belongs in the experience you chose. Other participants may see your display identity, your participation, and the answers, reactions, quiz content, or results that the relevant game is designed to reveal. Private choices stay hidden until the game rules say they should be revealed.</p>
        </Section>

        <Section title="5. Service providers and Telegram">
          <p>UTAGE relies on Telegram to launch the Mini App, verify Telegram identity, deliver bot messages, and support Telegram-native sharing. We also use infrastructure providers to host the application, database, files, and media. Optional background content-generation services may be used to create UTAGE question content; the system is designed not to send your Telegram identity or private Say It/Care message text to those content-generation providers.</p>
        </Section>

        <Section title="6. Selling data and advertising">
          <p>UTAGE does not sell personal data and does not use your personal data for third-party targeted advertising.</p>
        </Section>

        <Section title="7. Retention">
          <p>We keep account, connection, gameplay, safety, and content records for as long as they are needed to provide the feature, preserve history and recovery, protect users, resolve disputes, or meet legal and operational requirements. Product analytics events are automatically pruned after approximately 180 days. Some expired or cancelled interaction content is deleted or suppressed earlier by the application.</p>
        </Section>

        <Section title="8. Safety and moderation">
          <p>If you block or report another person, UTAGE stores the information needed to apply that safety choice and, when necessary, review the relevant interaction. Blocking can also suppress active connection messaging between the affected users.</p>
        </Section>

        <Section title="9. Security">
          <p>UTAGE validates Telegram Mini App authorization data before trusting account identity. We use access controls, signed action flows, rate limits, server-authoritative game state, and other technical safeguards intended to reduce unauthorized access, impersonation, replay abuse, and accidental data exposure. No online service can guarantee absolute security.</p>
        </Section>

        <Section title="10. Your choices">
          <p>You can choose whether to join invitations, use matchmaking, create or answer quizzes, upload custom images, schedule Care messages, or use sharing features. You can also use UTAGE safety tools to block or report people you encountered. For privacy questions, access, correction, or deletion requests, contact UTAGE through the official Telegram bot <strong>@UtagePlayBot</strong>.</p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>We may update this policy as UTAGE changes. When a material change affects how personal information is handled, we will update the effective date and make the current policy available at this URL.</p>
        </Section>

        <footer className="privacy-footer">
          <p>UTAGE · Privacy Policy</p>
          <a href="https://t.me/UtagePlayBot">Open @UtagePlayBot</a>
        </footer>
      </div>
    </main>
  );
}
